import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useBookingScreenEntry } from '../lib/useBookingScreenEntry'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Users, Calendar, CreditCard, BedDouble, CheckCircle } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import BackPillButton from '../components/BackPillButton'
import { commitMemberBookingSuccess, createCommittedBooking } from '../lib/upcomingBookingsCache'

function formatDateRange(checkInDate, checkOutDate) {
  if (!checkInDate || !checkOutDate) return null
  return `${checkInDate} → ${checkOutDate}`
}

function parseRoomsPayload(serialized, fallback = [{ adults: 2, children: 0 }]) {
  if (!serialized) return fallback
  try {
    const parsed = JSON.parse(serialized)
    if (!Array.isArray(parsed) || parsed.length === 0) return fallback
    return parsed.map((room) => ({
      adults: Math.max(1, Number.parseInt(room?.adults, 10) || 1),
      children: Math.max(0, Number.parseInt(room?.children, 10) || 0),
    }))
  } catch (_error) {
    return fallback
  }
}

export default function MemberAccommodationBookingPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const initialRooms = useMemo(() => {
    const fromState = Array.isArray(location.state?.rooms) ? location.state.rooms : null
    const fromQuery = searchParams.get('rooms')
    return parseRoomsPayload(fromQuery, parseRoomsPayload(JSON.stringify(fromState || []), [{ adults: 2, children: 0 }]))
  }, [location.state?.rooms, searchParams])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [checkInDate, setCheckInDate] = useState(searchParams.get('checkInDate') || '')
  const [checkOutDate, setCheckOutDate] = useState(searchParams.get('checkOutDate') || '')
  const [rooms, setRooms] = useState(initialRooms)
  const [specialRequests, setSpecialRequests] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [settlementMethod, setSettlementMethod] = useState(searchParams.get('settlementMethod') || 'card_on_arrival')
  const [formError, setFormError] = useState(null)
  const [submittedReservation, setSubmittedReservation] = useState(null)
  useBookingScreenEntry(submittedReservation ? 'confirmed' : 'review')
  const [carouselIndex, setCarouselIndex] = useState(0)

  const roomTypeFromQuery = searchParams.get('roomType') || location.state?.roomType || ''

  useEffect(() => {
    let cancelled = false

    async function loadRoomTypes() {
      if (!clubCode) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/hotel/room-types?rooms=${encodeURIComponent(JSON.stringify(rooms))}`)
        const json = await res.json().catch(() => null)

        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_room_types')
        }

        const types = Array.isArray(json?.roomTypes) ? json.roomTypes : []
        if (cancelled) return

        const matchedType = types.find((type) => String(type?.roomType || '') === String(roomTypeFromQuery || '')) || null
        if (!matchedType || matchedType.isEligibleForRequest === false) {
          navigate(`/${clubCode}/accommodations`, { replace: true })
          return
        }

        setSelectedType(matchedType)
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load room types', err)
          setError("portalPresentation.member_we_could_not_load_accommodations_right_now_please_try_again_shortly")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadRoomTypes()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet, navigate, roomTypeFromQuery, rooms])

  const guestsCount = useMemo(() => rooms.reduce((sum, room) => sum + room.adults + room.children, 0), [rooms])
  const roomCount = rooms.length

  const photoStack = useMemo(() => {
    if (!selectedType) return []
    return (selectedType.photoUrls || []).slice(0, 10)
  }, [selectedType])

  const currentPhoto = selectedType && photoStack.length > 0 ? photoStack[carouselIndex] : null

  const canSubmit = selectedType && checkInDate && checkOutDate && checkOutDate > checkInDate && roomCount > 0
  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate || checkOutDate <= checkInDate) return 1
    const from = new Date(`${checkInDate}T00:00:00.000Z`)
    const to = new Date(`${checkOutDate}T00:00:00.000Z`)
    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000))
  }, [checkInDate, checkOutDate])
  const nightlyRate = useMemo(() => Number(selectedType?.nightlyRate || 0), [selectedType?.nightlyRate])
  const currencyCode = useMemo(() => selectedType?.currencyCode || 'GBP', [selectedType?.currencyCode])
  const totalEstimate = useMemo(
    () => Math.max(0, Number((nightlyRate * nights * roomCount).toFixed(2))),
    [nightlyRate, nights, roomCount]
  )
  const hasValidRate = Number.isFinite(nightlyRate) && nightlyRate > 0

  const handleSubmitRequest = async () => {
    setFormError(null)
    setSubmittedReservation(null)

    if (!canSubmit) {
      setFormError("portalPresentation.member_please_choose_valid_dates_before_requesting_a_booking")
      return
    }
    if (!hasValidRate) {
      setFormError("portalPresentation.member_this_room_type_does_not_yet_have_a_configured_rate_please_contact_the_club")
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        checkInDate,
        checkOutDate,
        guestsCount,
        roomCount,
        rooms,
        requestedRoomType: selectedType?.roomType,
        specialRequests: specialRequests.trim() || null,
        settlementMethod,
        paymentPlan: {
          settlementMethod,
        },
      }
      const res = await memberPortalApiPost(
        `/api/members-portal/${clubCode}/hotel/reservations`,
        payload
      )
      const json = await res.json().catch(() => null)

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || 'booking_request_failed')
      }

      const reservation = json?.reservation || null
      if (!reservation?.id) throw new Error('We could not verify the booking outcome. Check your upcoming bookings or contact the club before trying again.')
      setSubmittedReservation(reservation)
      const booking = createCommittedBooking({ id: reservation?.id || json?.booking_id, module: 'hotel', title: selectedType?.roomType,
        location: selectedType?.roomType, startAt: reservation?.check_in_at || `${checkInDate}T15:00:00`, timeZone: reservation?.time_zone,
        imageUrl: currentPhoto, amount: totalEstimate, currency: currencyCode,
        details: { status: reservation?.status || 'pending', payment_method: settlementMethod, payment_summary: settlementMethod === 'member_account' ? 'Charged to member account' : 'Payment arranged' } })
      commitMemberBookingSuccess(booking, { clubCode })
    } catch (err) {
      console.error('Unable to request hotel booking', err)
      setFormError("portalPresentation.member_we_were_unable_to_submit_your_request_please_try_again")
    }
    setSubmitting(false)
  }

  return (
    <div className="mp-page mp-page--full mp-accommodation-booking">
      <div className="mp-accommodation-booking__header">
        <BackPillButton className="mp-accommodation-booking__back" to={`/${clubCode}/accommodations`}> {memberText("portalPresentation.member_back_to_accommodations")} </BackPillButton>
        <div className="mp-accommodation-booking__title">
          <p className="mp-eyebrow">{memberText("portalPresentation.member_accommodations")}</p>
          <h3> {memberText("portalPresentation.member_review")} <strong>{memberText("portalPresentation.member_your_booking")}</strong>
          </h3>
          <p className="mp-text-muted">{memberText("portalPresentation.member_confirm_occupancy_settlement_method_and_submit_your_booking_request")}</p>
        </div>
        <div aria-hidden className="mp-accommodation-booking__spacer" />
      </div>

      {error ? <div className="mp-inline-error">{memberText(error)}</div> : null}

      {!loading && selectedType ? (
        submittedReservation ? (
          <div className="mp-card mp-card--center mp-booking-success">
            <CheckCircle size={48} className="mp-success-icon" />
            <h2>{memberText("portalPresentation.member_booking_request_confirmed")}</h2>
            <p className="mp-text-muted">{memberText("portalPresentation.member_your_accommodation_request_is_now_pending_confirmation_from_the_club_team")}</p>
            <p className="mp-label">{memberText("portalPresentation.member_reservation_reference")} {submittedReservation?.id || memberText("portalPresentation.member_pending_confirmation")}</p>
            <p className="mp-label">{memberText("portalPresentation.member_dates")} {formatDateRange(submittedReservation?.check_in_date || checkInDate, submittedReservation?.check_out_date || checkOutDate)}</p>
            <button type="button" className="mp-btn mp-btn--primary" onClick={() => navigate(`/${clubCode}/accommodations`)}> {memberText("portalPresentation.member_back_to_accommodations")} </button>
          </div>
        ) : (
        <div className="mp-accommodation-booking-layout">
          <div className="mp-accommodation-booking__left">
            <section className="mp-card"><div className="mp-card__body"><div className="mp-accommodation-carousel">{currentPhoto ? <img src={currentPhoto} alt={selectedType.roomType} /> : <div className="mp-accommodation-card__placeholder">{memberText("portalPresentation.member_photos_coming_soon")}</div>}{photoStack.length > 1 ? <div className="mp-accommodation-carousel__controls"><button type="button" onClick={() => setCarouselIndex((prev) => (prev - 1 + photoStack.length) % photoStack.length)} aria-label={memberText("portalPresentation.member_previous_photo")}><ChevronLeft size={18} /></button><button type="button" onClick={() => setCarouselIndex((prev) => (prev + 1) % photoStack.length)} aria-label={memberText("portalPresentation.member_next_photo")}><ChevronRight size={18} /></button></div> : null}</div></div></section>
            <section className="mp-card"><div className="mp-card__header"><div><p className="mp-eyebrow">{memberText("portalPresentation.member_occupancy")}</p><h4>{memberText("portalPresentation.member_room_plan")}</h4></div></div><div className="mp-card__body"><div className="mp-accommodation-room-list">{rooms.map((room, index) => <div key={`review-room-${index}`} className="mp-summary-row subtle"><span className="mp-label">{memberText("portalPresentation.member_room")} {index + 1}</span><span className="mp-value">{room.adults} {memberText("portalPresentation.member_adults")} {room.children} {memberText("portalPresentation.member_children")}</span></div>)}</div></div></section>
            <section className="mp-card"><div className="mp-card__header"><div><p className="mp-eyebrow">{memberText("golf.bookingModal.requests")}</p><h4>{memberText("portalPresentation.member_anything_we_should_prepare")}</h4></div></div><div className="mp-card__body"><label className="mp-accommodation-requests"><span>{memberText("portalPresentation.member_special_requests_optional")}</span><textarea rows={4} value={specialRequests} onChange={(event) => setSpecialRequests(event.target.value)} placeholder={memberText("portalPresentation.member_late_arrival_adjoining_rooms_dietary_preferences_or_anything_else")} /></label></div></section>
          </div>

          <aside className="mp-accommodation-booking__summary sticky top-6">
            <div className="mp-summary-card">
              <div className="mp-summary-header"><p className="mp-eyebrow">{memberText("facilityBooking.review")}</p><h4>{memberText("portalPresentation.member_review_confirm")}</h4></div>
              <div className="mp-summary-section">
                <div className="mp-summary-row"><BedDouble size={16} /><div><p className="mp-label">{memberText("portalPresentation.member_room_type")}</p><p className="mp-value">{selectedType?.roomType || memberText("portalPresentation.member_select_a_room_type")}</p></div></div>
                <div className="mp-summary-row"><Calendar size={16} /><div><p className="mp-label">{memberText("portalPresentation.member_dates")}</p><p className="mp-value">{formatDateRange(checkInDate, checkOutDate) || memberText("portalPresentation.member_select_dates")}</p></div></div>
                <div className="mp-summary-row"><Calendar size={16} /><div><p className="mp-label">{memberText("portalPresentation.member_nights")}</p><p className="mp-value">{nights}</p></div></div>
                <div className="mp-summary-row"><Users size={16} /><div><p className="mp-label">{memberText("portalPresentation.member_rooms_guests")}</p><p className="mp-value">{roomCount} {memberText("portalPresentation.member_rooms")} {guestsCount} {memberText("portalPresentation.member_guests")}</p></div></div>
              </div>

              <div className="mp-summary-section total">
                <div className="mp-summary-row"><CreditCard size={16} /><div><p className="mp-label">{memberText("portalPresentation.member_nightly_rate_per_room")}</p><p className="mp-value">{currencyCode} {new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(nightlyRate)}</p></div></div>
                <div><p className="mp-label">{memberText("portalPresentation.member_total")}</p><p className="mp-summary-total">{currencyCode} {new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalEstimate)}</p></div>
              </div>

              <div className="mp-summary-section">
                <p className="mp-label">{memberText("facilityBooking.payment")}</p>
                <div className="mp-payment-options">
                  <label className="mp-payment"><input type="radio" name="settlement" checked={settlementMethod === 'member_charge'} onChange={() => setSettlementMethod('member_charge')} /><div><div className="mp-value">{memberText("portalPresentation.member_add_to_member_account")}</div></div></label>
                  <label className="mp-payment"><input type="radio" name="settlement" checked={settlementMethod === 'card_online'} onChange={() => setSettlementMethod('card_online')} /><div><div className="mp-value">{memberText("portalPresentation.member_pay_by_card_online")}</div></div></label>
                  <label className="mp-payment"><input type="radio" name="settlement" checked={settlementMethod === 'card_on_arrival'} onChange={() => setSettlementMethod('card_on_arrival')} /><div><div className="mp-value">{memberText("portalPresentation.member_pay_on_arrival")}</div></div></label>
                </div>
              </div>

              {!hasValidRate ? <div className="mp-inline-error">{memberText("portalPresentation.member_this_room_type_does_not_yet_have_a_configured_rate_please_contact_the_club")}</div> : null}
              {formError ? <div className="mp-inline-error">{memberText(formError)}</div> : null}
              <button type="button" className="mp-btn mp-btn--primary mp-btn--block" onClick={handleSubmitRequest} disabled={submitting || !canSubmit || !hasValidRate || Boolean(submittedReservation)}>
                {submitting ? memberText("epos.messages.sending") : memberText("portalPresentation.member_confirm_booking")}
              </button>
            </div>
          </aside>
        </div>
        )
      ) : null}
    </div>
  )
}
